'use client';

import { useEffect, useRef } from 'react';

import {
  FREE_LIMIT_EVENT_NAME,
  INSUFFICIENT_COINS_EVENT_NAME,
  type InsufficientCoinsDetail,
} from '~/lib/auth-error';

import { useOutOfCoins } from './out-of-coins-context';

/**
 * Listens for 'wallet:insufficient-coins' and 'chat:free-limit' custom DOM
 * events and opens the focused upsell popup (out-of-coins dialog — it sells
 * the subscription trial, which is the answer to both walls), forwarding the
 * "need X / have Y" amounts when the event carries them. Must be mounted
 * inside <OutOfCoinsProvider>.
 */
export function InsufficientCoinsWatcher() {
  const { openOutOfCoins, isOpen } = useOutOfCoins();

  // Keep the latest open state in a ref so the event listener can read it
  // without being re-subscribed every time the popup opens or closes.
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const handler = (event: Event) => {
      if (isOpenRef.current) return;
      const detail = (event as CustomEvent<InsufficientCoinsDetail>).detail;
      openOutOfCoins(detail ?? {});
    };

    window.addEventListener(INSUFFICIENT_COINS_EVENT_NAME, handler);
    window.addEventListener(FREE_LIMIT_EVENT_NAME, handler);
    return () => {
      window.removeEventListener(INSUFFICIENT_COINS_EVENT_NAME, handler);
      window.removeEventListener(FREE_LIMIT_EVENT_NAME, handler);
    };
  }, [openOutOfCoins]);

  return null;
}
